import React, { useEffect, useState } from 'react'
import adminApi from './api'

interface Setting {
  id: number
  key: string
  value: string
  description: string
  created_at: string
  updated_at: string
}

export const SettingsAdmin: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    fetchSettings()
  }, [])

  const HIDDEN_KEYS = new Set([
    'use_new_certificate_design',
    'sponsor_logos_top',
    'sponsor_logos_bottom',
    'handout_images',
    'send_handouts'
  ])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const { data } = await adminApi.get('/api/admin/settings')
      const all: Setting[] = data.settings || []
      setSettings(all.filter(s => !HIDDEN_KEYS.has(s.key)))
    } catch (err: any) {
      console.error('Error fetching settings:', err)
      setError('Не удалось загрузить настройки')
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = async (key: string, currentValue: string) => {
    try {
      setSaving(key)
      setError(null)
      setSuccess(null)

      const newValue = currentValue === 'true' ? 'false' : 'true'

      await adminApi.put(`/api/admin/settings/${key}`, {
        value: newValue
      })

      setSettings(prev =>
        prev.map(s => (s.key === key ? { ...s, value: newValue } : s))
      )

      setSuccess('Настройка успешно обновлена')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      console.error('Error updating setting:', err)
      setError('Не удалось обновить настройку')
    } finally {
      setSaving(null)
    }
  }

  const getSettingLabel = (key: string): string => {
    const labels: Record<string, string> = {
      tickets_enabled: 'Включить покупку билетов',
      homepage_show_event_block: 'Показывать блок предстоящего события на главной'
    }
    return labels[key] || key
  }

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Настройки</h1>
            <p className="page-subtitle">Управление системными настройками приложения</p>
          </div>
        </div>
        <p className="text-center py-10 text-gray-500">
          Загрузка...
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Настройки</h1>
          <p className="page-subtitle">Управление системными настройками приложения</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-5">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success mb-5">
          {success}
        </div>
      )}

      <div className="table-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Настройка</th>
              <th className="w-[130px] text-center">Значение</th>
            </tr>
          </thead>
          <tbody>
            {settings.length === 0 ? (
              <tr>
                <td colSpan={2}>
                  <div className="empty-state">
                    <div className="empty-state-text">Настройки не найдены</div>
                  </div>
                </td>
              </tr>
            ) : (
              settings.map(setting => (
                <tr key={setting.key}>
                  <td>
                    <div className="font-medium">
                      {getSettingLabel(setting.key)}
                    </div>
                    {setting.description && (
                      <div className="text-[13px] text-gray-500 mt-1">
                        {setting.description}
                      </div>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => handleToggle(setting.key, setting.value)}
                      disabled={saving === setting.key}
                      className={`relative w-[52px] h-7 rounded-full border-0 transition-colors duration-200 ${
                        setting.value === 'true' ? 'bg-[#52c41a]' : 'bg-[#d9d9d9]'
                      } ${saving === setting.key ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                    >
                      <span
                        className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-[left] duration-200 ${
                          setting.value === 'true' ? 'left-[26px]' : 'left-0.5'
                        }`}
                      />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
